import { useState, useEffect, useCallback } from 'react';

/**
 * Hook genérico para operações CRUD
 * Reduz código duplicado em páginas de lista/CRUD
 * 
 * @param {Object} service - Service com métodos getAll, create, update, delete
 * @param {string} resourceName - Nome do recurso para mensagens (ex: 'serviço', 'profissional')
 * @returns {Object} Estado e métodos CRUD
 * 
 * @example
 * const { items, loading, error, reload, create, update, delete: deleteFn } = useCRUD(servicesService, 'serviço');
 */
export const useCRUD = (service, resourceName = 'item') => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadItems = useCallback(async () => {
    console.log('[useCRUD] loadItems chamado - timestamp:', Date.now());
    try {
      setLoading(true);
      setError(null);
      console.log('[useCRUD] Chamando service.getAll()...');
      const data = await service.getAll();
      console.log('[useCRUD] service.getAll() retornou:', Array.isArray(data) ? `${data.length} items` : data);
      // service.getAll() já retorna os dados extraídos pelo extractData
      const items = Array.isArray(data) ? data : [];
      setItems(items);
    } catch (err) {
      console.error(`[useCRUD] Erro ao carregar ${resourceName}s:`, err);
      setError(err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [service, resourceName]);

  const createItem = useCallback(async (data) => {
    const result = await service.create(data);
    await loadItems(); // Recarrega lista após criar
    return result;
  }, [service, loadItems]);

  const updateItem = useCallback(async (id, data) => {
    const result = await service.update(id, data);
    await loadItems(); // Recarrega lista após atualizar
    return result;
  }, [service, loadItems]);

  const deleteItem = useCallback(async (id) => {
    console.log('[useCRUD] deleteItem chamado com ID:', id);
    console.log('[useCRUD] service:', service);
    console.log('[useCRUD] service.delete:', typeof service.delete);
    
    try {
      console.log('[useCRUD] Chamando service.delete...');
      const result = await service.delete(id);
      console.log('[useCRUD] service.delete retornou:', result);
      
      console.log('[useCRUD] Recarregando lista...');
      await loadItems(); // Recarrega lista após deletar
      console.log('[useCRUD] Lista recarregada');
      
      return result;
    } catch (error) {
      console.error('[useCRUD] ERRO em deleteItem:', error);
      console.error('[useCRUD] Error stack:', error.stack);
      throw error;
    }
  }, [service, loadItems]);

  useEffect(() => {
    console.log('[useCRUD] useEffect disparado - chamando loadItems()');
    loadItems();
  }, [loadItems]);

  return {
    items,
    loading,
    error,
    reload: loadItems,
    create: createItem,
    update: updateItem,
    deleteItem: deleteItem, // Usar deleteItem ao invés de delete (palavra reservada)
    delete: deleteItem, // Mantém compatibilidade com código antigo
  };
};

export default useCRUD;
